import React, { useRef, useState, useEffect, useCallback } from 'react';
import { RotateCcw, Check, X, ShieldAlert, Sparkles, Grid3X3, ArrowRight } from 'lucide-react';

interface PatternLockModalProps {
  isOpen: boolean;
  initialPattern?: string; // e.g. "1-2-3-6-9" or "Desen: 1-2-5-8"
  onClose: () => void;
  onSave: (patternStr: string) => void;
}

// 3x3 Grid Points
// 1 2 3
// 4 5 6
// 7 8 9
interface Point {
  id: number;
  x: number;
  y: number;
}

/**
 * Parses pattern string and extracts 3x3 dot indices (1-9).
 * Returns array of dots ONLY if it's explicitly a pattern (e.g. "Desen: 1-2-3-6-9" or "1-2-5-8").
 * Plain numeric PINs/passwords (e.g. "123", "1234", "2580") are NOT treated as patterns.
 */
export function extractPatternDots(str?: string): number[] {
  if (!str) return [];
  const trimmed = str.trim();
  const lower = trimmed.toLowerCase();

  // 1. If it contains "desen" (e.g. "Desen: 1-2-3-6" or "desen: 1-2-5" or "desen 1-2-3")
  if (lower.includes('desen')) {
    // Check for hyphenated sequence first: 1-2-3-6
    const hyphenMatch = trimmed.match(/([1-9](?:-[1-9])+)/);
    if (hyphenMatch) {
      return hyphenMatch[1].split('-').map(Number).filter(n => n >= 1 && n <= 9);
    }
    // Check for direct digits after word desen, e.g. "Desen: 1236"
    const digitsMatch = trimmed.match(/desen[^\d]*([1-9]{2,9})/i);
    if (digitsMatch) {
      return digitsMatch[1].split('').map(Number).filter(n => n >= 1 && n <= 9);
    }
  }

  // 2. If it is strictly hyphenated dots (at least 2 dots connected by hyphen, e.g. "1-2-3" or "1-2-5-8-9")
  // Must have at least one hyphen (e.g. [1-9]-[1-9]...) so plain "123" or "1" is NEVER matched
  const strictHyphenMatch = trimmed.match(/^([1-9](?:-[1-9])+)(?:\s.*)?$/);
  if (strictHyphenMatch) {
    return strictHyphenMatch[1].split('-').map(Number).filter(n => n >= 1 && n <= 9);
  }

  // Otherwise, it's a regular password / PIN (like "123", "1234", "admin123", "2580") - NOT a pattern
  return [];
}

export function isPatternPassword(str?: string): boolean {
  return extractPatternDots(str).length >= 2;
}

export const PatternLockModal: React.FC<PatternLockModalProps> = ({
  isOpen,
  initialPattern = '',
  onClose,
  onSave,
}) => {
  const [selectedDots, setSelectedDots] = useState<number[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentPos, setCurrentPos] = useState<{ x: number; y: number } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  // Parse initial pattern if exists (e.g. "Desen: 1-2-5-8-9" or "1-2-5-8")
  useEffect(() => {
    if (isOpen) {
      if (initialPattern) {
        const dots = extractPatternDots(initialPattern);
        setSelectedDots(dots);
      } else {
        setSelectedDots([]);
      }
      setIsDrawing(false);
      setCurrentPos(null);
    }
  }, [isOpen, initialPattern]);

  if (!isOpen) return null;

  // Grid coordinates inside a 300x300 SVG viewbox
  const GRID_POINTS: Point[] = [
    { id: 1, x: 50, y: 50 },
    { id: 2, x: 150, y: 50 },
    { id: 3, x: 250, y: 50 },
    { id: 4, x: 50, y: 150 },
    { id: 5, x: 150, y: 150 },
    { id: 6, x: 250, y: 150 },
    { id: 7, x: 50, y: 250 },
    { id: 8, x: 150, y: 250 },
    { id: 9, x: 250, y: 250 },
  ];

  const getSvgCoordinates = (e: React.MouseEvent | React.TouchEvent | MouseEvent | TouchEvent) => {
    if (!svgRef.current) return null;
    const rect = svgRef.current.getBoundingClientRect();
    const clientX = 'touches' in e && e.touches.length > 0 ? e.touches[0].clientX : (e as MouseEvent).clientX;
    const clientY = 'touches' in e && e.touches.length > 0 ? e.touches[0].clientY : (e as MouseEvent).clientY;

    if (clientX === undefined || clientY === undefined) return null;

    const scaleX = 300 / rect.width;
    const scaleY = 300 / rect.height;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const getPointUnderCoords = (pos: { x: number; y: number } | null) => {
    if (!pos) return null;
    const HIT_RADIUS = 30; // Radius around dot
    return GRID_POINTS.find(p => {
      const dx = p.x - pos.x;
      const dy = p.y - pos.y;
      return Math.sqrt(dx * dx + dy * dy) <= HIT_RADIUS;
    });
  };

  const handleStartDraw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const pos = getSvgCoordinates(e);
    if (!pos) return;

    setIsDrawing(true);
    setCurrentPos(pos);

    const hit = getPointUnderCoords(pos);
    if (hit) {
      setSelectedDots([hit.id]);
    } else {
      setSelectedDots([]);
    }
  };

  const handleMoveDraw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing) return;
    e.preventDefault();
    const pos = getSvgCoordinates(e);
    if (!pos) return;

    setCurrentPos(pos);
    const hit = getPointUnderCoords(pos);

    if (hit && !selectedDots.includes(hit.id)) {
      setSelectedDots(prev => [...prev, hit.id]);
    }
  };

  const handleEndDraw = () => {
    setIsDrawing(false);
    setCurrentPos(null);
  };

  const handleDotClick = (dotId: number) => {
    if (isDrawing) return;
    if (selectedDots.includes(dotId)) {
      // Toggle or reset from this dot
      const idx = selectedDots.indexOf(dotId);
      if (idx === selectedDots.length - 1) {
        setSelectedDots(prev => prev.slice(0, -1));
      }
    } else {
      setSelectedDots(prev => [...prev, dotId]);
    }
  };

  const handleReset = () => {
    setSelectedDots([]);
    setIsDrawing(false);
    setCurrentPos(null);
  };

  const handleSave = () => {
    if (selectedDots.length === 0) {
      onSave('');
    } else {
      const patternText = `Desen: ${selectedDots.join('-')}`;
      onSave(patternText);
    }
    onClose();
  };

  // Helper to get Point coords
  const getPoint = (id: number) => GRID_POINTS.find(p => p.id === id);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/75 backdrop-blur-xs animate-in fade-in duration-150">
      <div className="min-h-full flex items-center justify-center p-3 sm:p-4 text-center">
        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full border border-slate-200 overflow-hidden flex flex-col my-auto text-left">
        {/* Header */}
        <div className="bg-slate-900 text-white px-4 py-3 flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center space-x-2.5">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center shadow-xs">
              <Grid3X3 className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-sm text-white">Ekran Kilit Deseni Çiz</h3>
              <p className="text-[11px] text-slate-400">Noktaları sırayla birleştirin</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white p-1 rounded-lg transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Interactive Pattern Area */}
        <div className="p-4 flex flex-col items-center bg-slate-50 border-b border-slate-200 select-none">
          <div className="relative w-[280px] h-[280px] bg-white rounded-2xl shadow-inner border border-slate-200 p-2 touch-none flex items-center justify-center">
            <svg
              ref={svgRef}
              viewBox="0 0 300 300"
              className="w-full h-full cursor-crosshair touch-none"
              onMouseDown={handleStartDraw}
              onMouseMove={handleMoveDraw}
              onMouseUp={handleEndDraw}
              onMouseLeave={handleEndDraw}
              onTouchStart={handleStartDraw}
              onTouchMove={handleMoveDraw}
              onTouchEnd={handleEndDraw}
            >
              {/* Lines between connected dots */}
              {selectedDots.map((dotId, index) => {
                if (index === 0) return null;
                const prev = getPoint(selectedDots[index - 1]);
                const curr = getPoint(dotId);
                if (!prev || !curr) return null;
                return (
                  <line
                    key={`line-${index}`}
                    x1={prev.x}
                    y1={prev.y}
                    x2={curr.x}
                    y2={curr.y}
                    stroke="#3b82f6"
                    strokeWidth="6"
                    strokeLinecap="round"
                    className="transition-all"
                  />
                );
              })}

              {/* Line to current dragging position */}
              {isDrawing && currentPos && selectedDots.length > 0 && (
                <line
                  x1={getPoint(selectedDots[selectedDots.length - 1])?.x || 0}
                  y1={getPoint(selectedDots[selectedDots.length - 1])?.y || 0}
                  x2={currentPos.x}
                  y2={currentPos.y}
                  stroke="#93c5fd"
                  strokeWidth="4"
                  strokeDasharray="4,4"
                  strokeLinecap="round"
                />
              )}

              {/* 9 Grid Dots */}
              {GRID_POINTS.map(p => {
                const isSelected = selectedDots.includes(p.id);
                const order = selectedDots.indexOf(p.id);
                const isFirst = order === 0;
                const isLast = order === selectedDots.length - 1 && selectedDots.length > 0;

                return (
                  <g
                    key={p.id}
                    onClick={() => handleDotClick(p.id)}
                    className="cursor-pointer group"
                  >
                    {/* Outer aura on hover or active */}
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={isSelected ? 26 : 22}
                      fill={isSelected ? '#dbeafe' : '#f8fafc'}
                      stroke={isSelected ? '#3b82f6' : '#cbd5e1'}
                      strokeWidth={isSelected ? 3 : 1.5}
                      className="transition-all duration-150"
                    />

                    {/* Inner core dot */}
                    <circle
                      cx={p.x}
                      cy={p.y}
                      r={isSelected ? 10 : 8}
                      fill={isSelected ? (isFirst ? '#2563eb' : isLast ? '#1d4ed8' : '#3b82f6') : '#64748b'}
                      className="transition-all duration-150"
                    />

                    {/* Step order badge number */}
                    {isSelected && (
                      <text
                        x={p.x}
                        y={p.y + 4}
                        textAnchor="middle"
                        fill="#ffffff"
                        fontSize="11"
                        fontWeight="bold"
                        fontFamily="monospace"
                        pointerEvents="none"
                      >
                        {order + 1}
                      </text>
                    )}
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Sequence info & preview */}
          <div className="mt-3 w-full bg-white p-2.5 rounded-xl border border-slate-200 shadow-2xs flex items-center justify-between">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-[11px] font-bold text-slate-500 shrink-0">Desen:</span>
              {selectedDots.length > 0 ? (
                <div className="flex items-center gap-1 overflow-x-auto py-0.5">
                  {selectedDots.map((dot, idx) => (
                    <React.Fragment key={idx}>
                      <span className="w-5 h-5 rounded-md bg-blue-600 text-white text-[11px] font-mono font-bold flex items-center justify-center shrink-0">
                        {dot}
                      </span>
                      {idx < selectedDots.length - 1 && (
                        <span className="text-slate-300 text-xs shrink-0">→</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <span className="text-xs text-slate-400 italic">Henüz desen çizilmedi</span>
              )}
            </div>

            <button
              type="button"
              onClick={handleReset}
              disabled={selectedDots.length === 0}
              className="text-xs text-slate-500 hover:text-rose-600 p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:pointer-events-none transition-colors cursor-pointer shrink-0"
              title="Temizle"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Quick Helper Tips */}
        <div className="px-4 py-2.5 bg-slate-50 text-[11px] text-slate-500 border-b border-slate-200">
          <p className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0"></span>
            <span>Fare veya parmağınızla noktalar üzerinde kaydırarak çizin.</span>
          </p>
        </div>

        {/* Footer Actions */}
        <div className="p-3 bg-white flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-2 text-slate-600 hover:bg-slate-100 text-xs font-bold rounded-xl transition-colors cursor-pointer"
          >
            İptal
          </button>

          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-xl shadow-xs transition-colors flex items-center gap-1.5 cursor-pointer"
          >
            <Check className="w-4 h-4" />
            <span>Deseni Kaydet</span>
          </button>
        </div>
      </div>
      </div>
    </div>
  );
};

/**
 * Visual Mini Pattern Indicator Badge / Component
 * Renders a tiny 3x3 visual of the drawn pattern to display on tickets, summaries, cards.
 */
export const PatternThumbnail: React.FC<{ patternStr?: string; size?: number; className?: string }> = ({
  patternStr,
  size = 36,
  className = '',
}) => {
  if (!patternStr) return null;

  // Extract dots (only if string is genuinely a pattern, not a PIN/password like "123")
  const dots = extractPatternDots(patternStr);
  if (dots.length < 2) return null;

  const GRID_POINTS: Point[] = [
    { id: 1, x: 15, y: 15 },
    { id: 2, x: 50, y: 15 },
    { id: 3, x: 85, y: 15 },
    { id: 4, x: 15, y: 50 },
    { id: 5, x: 50, y: 50 },
    { id: 6, x: 85, y: 50 },
    { id: 7, x: 15, y: 85 },
    { id: 8, x: 50, y: 85 },
    { id: 9, x: 85, y: 85 },
  ];

  const getPoint = (id: number) => GRID_POINTS.find(p => p.id === id);

  return (
    <div
      className={`inline-flex items-center justify-center bg-slate-900 rounded-lg p-1 border border-slate-700 shadow-2xs ${className}`}
      style={{ width: size, height: size }}
      title={`Kilit Deseni: ${dots.join(' → ')}`}
    >
      <svg viewBox="0 0 100 100" className="w-full h-full">
        {/* Lines */}
        {dots.map((dotId, index) => {
          if (index === 0) return null;
          const prev = getPoint(dots[index - 1]);
          const curr = getPoint(dotId);
          if (!prev || !curr) return null;
          return (
            <line
              key={`line-${index}`}
              x1={prev.x}
              y1={prev.y}
              x2={curr.x}
              y2={curr.y}
              stroke="#60a5fa"
              strokeWidth="8"
              strokeLinecap="round"
            />
          );
        })}

        {/* 9 Dots */}
        {GRID_POINTS.map(p => {
          const isSelected = dots.includes(p.id);
          const isStart = dots[0] === p.id;
          return (
            <circle
              key={p.id}
              cx={p.x}
              cy={p.y}
              r={isSelected ? 6.5 : 3.5}
              fill={isStart ? '#38bdf8' : isSelected ? '#ffffff' : '#475569'}
            />
          );
        })}
      </svg>
    </div>
  );
};
